import { api } from './api';

export type Musica = {
  id: number;
  /** vinculo com o catalogo; nulo so em repertorio antigo, de antes dele */
  musicaId: number | null;
  musica: string;
  artista: string | null;
  tom: string | null;
  link: string | null;
  observacao: string | null;
  ordem: number;
};

/** Entrada do catalogo de musicas da igreja, com o historico de uso. */
export type MusicaCatalogo = {
  id: number;
  titulo: string;
  artista: string | null;
  tom: string | null;
  link: string | null;
  observacao: string | null;
  ativo: boolean;
  vezes: number;
  ultimaVez: string | null;
};

export const buscarCatalogo = (busca = '') =>
  api<{ musicas: MusicaCatalogo[]; podeEditar: boolean }>(
    `/musicas${busca ? `?busca=${encodeURIComponent(busca)}` : ''}`,
  );

export const criarNoCatalogo = (dados: {
  titulo: string;
  artista?: string | null;
  tom?: string | null;
  link?: string | null;
}) => api<{ id: number }>('/musicas', { method: 'POST', body: JSON.stringify(dados) });

/** "12 de julho" — ou null quando a musica nunca foi cantada. */
export function ultimaVezLegivel(iso: string | null): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'long',
  }).format(new Date(iso));
}

export const buscarRepertorio = (eventoId: number) =>
  api<{ musicas: Musica[]; podeEditar: boolean }>(`/repertorio?evento=${eventoId}`);

export const criarMusica = (dados: {
  eventoId: number;
  /** quando vem do catalogo; sem ele, o titulo digitado entra la sozinho */
  musicaId?: number;
  musica: string;
  tom?: string | null;
  link?: string | null;
  observacao?: string | null;
}) => api<{ id: number }>('/repertorio', { method: 'POST', body: JSON.stringify(dados) });

export const removerMusica = (id: number) => api(`/repertorio/${id}`, { method: 'DELETE' });

export type Midia = {
  id: number;
  eventoId: number | null;
  eventoTitulo: string | null;
  eventoInicio: string | null;
  tipo: 'foto' | 'video';
  titulo: string | null;
  descricao: string | null;
  caminho: string | null;
  link: string | null;
  bytes: number;
  criadoEm: string | null;
};

export type Album = {
  eventoId: number;
  titulo: string;
  inicio: string | null;
  fotos: number;
  videos: number;
};

export const buscarAlbuns = (tipo?: 'foto' | 'video') =>
  api<{ albuns: Album[] }>(`/midias/albuns${tipo ? `?tipo=${tipo}` : ''}`);

export const buscarMidias = (eventoId?: number, tipo?: 'foto' | 'video') => {
  const q = new URLSearchParams();
  if (eventoId) q.set('evento', String(eventoId));
  if (tipo) q.set('tipo', tipo);
  return api<{ midias: Midia[]; podePublicar: boolean }>(`/midias?${q.toString()}`);
};

export const publicarMidia = (dados: {
  eventoId?: number | null;
  tipo: 'foto' | 'video';
  titulo?: string | null;
  descricao?: string | null;
  caminho?: string | null;
  link?: string | null;
  bytes?: number;
}) => api<{ id: number }>('/midias', { method: 'POST', body: JSON.stringify(dados) });

export const removerMidia = (id: number) => api(`/midias/${id}`, { method: 'DELETE' });

export const buscarEventosParaAlbum = () =>
  api<{ eventos: { id: number; titulo: string; inicio: string | null }[] }>('/midias/eventos');

/** Transforma link do YouTube em endereco de incorporacao. */
export function incorporarVideo(link: string): string | null {
  try {
    const u = new URL(link);
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname === 'youtu.be') return `https://www.youtube.com/embed${u.pathname}`;
    return null;
  } catch {
    return null;
  }
}

export function tamanhoLegivel(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
